import { useEffect, useRef, useState } from "react";
import { Control, Controller, FieldValues, UseFormGetValues, useFieldArray } from "react-hook-form";
import { createLoadOptions } from "../../hooks/useDebounce";
import { SearchAdminItem } from "../../http/types/items";
import { useItemsApi } from "../../http/useItems";
import { itemLabel } from "../../utils";
import SvgAdd from "../icons/Add";
import SvgDelete from "../icons/Delete";
import Button from "./Button";
import FormField from "./FormField";
import styles from "./ItemQuantityField.module.css";
import Numeric from "./Numeric";
import { CustomAsyncSelect } from "./select/CustomSelect";

interface ItemQuantityFieldArrayProps<TFieldValues extends FieldValues> {
  control: Control<TFieldValues>;
  name: string;
  getValues: UseFormGetValues<TFieldValues>;
  disabled?: boolean;
  itemLabelText?: string;
  quantityLabelText?: string;
  addButtonTitle?: string;
  deleteButtonTitle?: string;
  itemFieldName?: string;
  quantityFieldName?: string;
}

type FormRow = Record<string, any>;

const getValueByPath = (values: any, path: string) =>
  path.split(".").reduce((currentValue, fieldName) => currentValue?.[fieldName], values);

const getRowsByPath = (values: any, path: string): FormRow[] => {
  const rows = getValueByPath(values, path);
  return Array.isArray(rows) ? rows : [];
};

const isSearchAdminItem = (value: any): value is SearchAdminItem =>
  Boolean(value && typeof value === "object" && typeof value.id === "string");

const getItemId = (value: any) => (isSearchAdminItem(value) ? value.id : undefined);

const ItemQuantityFieldArray = <TFieldValues extends FieldValues>({
  control,
  name,
  getValues,
  disabled = false,
  itemLabelText = "Item",
  quantityLabelText = "Quantity per support",
  addButtonTitle = "Add",
  deleteButtonTitle = "Delete",
  itemFieldName = "item",
  quantityFieldName = "quantityPerSupport",
}: ItemQuantityFieldArrayProps<TFieldValues>) => {
  const itemsApi = useItemsApi();
  const [itemOptions, setItemOptions] = useState<SearchAdminItem[]>([]);
  const itemAborter = useRef(new AbortController());
  const itemSearchTerm = useRef("");
  const loadItemOptions = createLoadOptions(itemAborter, itemSearchTerm, itemsApi.getAdminItems);

  const { fields, append, remove } = useFieldArray({
    control,
    name: name as any,
  });

  useEffect(() => {
    loadItemOptions("")
      .then(setItemOptions)
      .catch(() => setItemOptions([]));
  }, []);

  const appendEmptyRow = () => {
    append({
      [itemFieldName]: null,
      [quantityFieldName]: null,
    } as any);
  };

  const isDuplicatedItem = (item: SearchAdminItem) => {
    const selectedItemId = getItemId(item);

    if (!selectedItemId) {
      return false;
    }

    const selectedRows = getRowsByPath(getValues(), name).filter(
      (row) => getItemId(row[itemFieldName]) === selectedItemId,
    );

    return selectedRows.length > 1;
  };

  return (
    <div className={styles.container}>
      <div className={styles.header}>
        <span className={styles.headerItem}>{itemLabelText}</span>
        <span className={styles.headerQuantity}>{quantityLabelText}</span>
      </div>

      {fields.map((field, index) => {
        const isLast = index === fields.length - 1;
        const canRemove = fields.length > 1;
        const itemPath = `${name}.${index}.${itemFieldName}`;
        const quantityPath = `${name}.${index}.${quantityFieldName}`;

        return (
          <div key={field.id} className={styles.row}>
            <Controller
              name={itemPath as any}
              control={control}
              rules={{
                required: true,
                validate: (value) => !isDuplicatedItem(value),
              }}
              render={({ field: controllerField, fieldState }) => (
                <FormField label="" error={fieldState.error} isOptional={false} className={styles.fieldWrapper}>
                  {({ labelId, isInvalid }) => (
                    <CustomAsyncSelect<SearchAdminItem>
                      name={controllerField.name}
                      value={isSearchAdminItem(controllerField.value) ? controllerField.value : null}
                      onBlur={controllerField.onBlur}
                      inputId={labelId}
                      defaultOptions={itemOptions}
                      loadOptions={loadItemOptions}
                      isClearable={false}
                      isDisabled={disabled}
                      getOptionLabel={itemLabel}
                      getOptionValue={(option) => option.id}
                      isInvalid={isInvalid}
                      onChange={(value) => controllerField.onChange(value ?? null)}
                    />
                  )}
                </FormField>
              )}
            />

            <Controller
              name={quantityPath as any}
              control={control}
              rules={{
                required: true,
                validate: (value) => typeof value === "number" && value > 0,
              }}
              render={({ field: controllerField, fieldState }) => (
                <FormField label="" error={fieldState.error} isOptional={false} className={styles.fieldWrapper}>
                  {({ labelId, isInvalid }) => (
                    <Numeric
                      id={labelId}
                      value={typeof controllerField.value === "number" ? controllerField.value : null}
                      isInvalid={isInvalid}
                      decimalScale={6}
                      allowNegative={false}
                      min={0}
                      disabled={disabled}
                      onBlur={(value) => {
                        controllerField.onChange(value);
                        controllerField.onBlur();
                      }}
                    />
                  )}
                </FormField>
              )}
            />

            <div className={styles.actions}>
              <Button
                glyph={SvgAdd}
                buttonProps={{
                  className: `${styles.actionButton} ${!isLast ? styles.hiddenAction : ""}`,
                  disabled: disabled || !isLast,
                  title: addButtonTitle,
                  onClick: appendEmptyRow,
                }}
              />

              <Button
                glyph={SvgDelete}
                type="secondary"
                buttonProps={{
                  className: `${styles.actionButton} ${!canRemove ? styles.hiddenAction : ""}`,
                  disabled: disabled || !canRemove,
                  title: deleteButtonTitle,
                  onClick: () => remove(index),
                }}
              />
            </div>
          </div>
        );
      })}
    </div>
  );
};

export default ItemQuantityFieldArray;
